import { useCallback, useEffect, useMemo, useState } from "react";
import { can, ROLE_LABELS } from "@aigov/domain";
import { api, keycloak, type LibraryDto, type Me, type SystemDto } from "./api";
import { ErrorNote, errMsg, useToast } from "./ui";
import { RegisterView } from "./views/Register";
import { SystemView } from "./views/System";
import { CrosswalkView } from "./views/Crosswalk";
import { MembersView } from "./views/Members";
import { AuditView } from "./views/Audit";
import { SourcesView } from "./views/Sources";
import { NewWorkspace } from "./views/NewWorkspace";
import { DisclaimerFooter, DisclaimerGate } from "./views/Disclaimer";
import { WatchView } from "./views/Watch";

type Tab = "register" | "system" | "crosswalk" | "members" | "audit" | "watch" | "sources";

function remember(key: string, value?: string) {
  try { if (value === undefined) return localStorage.getItem(key); localStorage.setItem(key, value); } catch { /* storage unavailable */ }
  return null;
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [library, setLibrary] = useState<LibraryDto | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(remember("aigov-tenant"));
  const [tab, setTab] = useState<Tab>((remember("aigov-tab") as Tab) || "register");
  const [systems, setSystems] = useState<SystemDto[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const { toast, node: toastNode } = useToast();

  const loadMe = useCallback(async () => {
    const m = await api<Me>("GET", "/me");
    setMe(m);
    setTenantId((cur) => (cur && m.tenants.some((t) => t.id === cur) ? cur : m.tenants[0]?.id ?? null));
  }, []);
  const loadLibrary = useCallback(() => api<LibraryDto>("GET", "/library").then(setLibrary), []);

  useEffect(() => { Promise.all([loadMe(), loadLibrary()]).catch((e) => setError(errMsg(e))); }, [loadMe, loadLibrary]);

  const tenant = me?.tenants.find((t) => t.id === tenantId) ?? null;
  const role = tenant?.role ?? null;
  const allowed = useCallback((a: Parameters<typeof can>[1]) => can(role, a, me?.platformAdmin), [role, me?.platformAdmin]);

  const loadSystems = useCallback(async () => {
    if (!tenantId) { setSystems([]); return; }
    try { setSystems(await api<SystemDto[]>("GET", `/tenants/${tenantId}/systems`)); setError(null); }
    catch (e) { setError(errMsg(e)); setSystems([]); }
  }, [tenantId]);

  useEffect(() => { setSystems(null); setSelected(null); if (tenantId) remember("aigov-tenant", tenantId); void loadSystems(); }, [tenantId, loadSystems]);

  const go = (t: Tab) => { setTab(t); remember("aigov-tab", t); window.scrollTo({ top: 0 }); };
  const upsert = (s: SystemDto) => setSystems((cur) => {
    const list = cur ?? [];
    const i = list.findIndex((x) => x.id === s.id);
    return i < 0 ? [...list, s].sort((a, b) => a.name.localeCompare(b.name)) : list.map((x) => (x.id === s.id ? s : x));
  });

  const tabs = useMemo(() => ([
    ["register", "Register", true],
    ["system", "System", true],
    ["crosswalk", "Crosswalk", true],
    ["members", "Members", allowed("member.read")],
    ["audit", "Audit trail", allowed("audit.read")],
    ["watch", "Regulatory watch", !!me?.platformAdmin],
    ["sources", "Notice & sources", true],
  ] as [Tab, string, boolean][]).filter((t) => t[2]), [allowed]);
  const activeTab = tabs.some((t) => t[0] === tab) ? tab : "register";

  if (!me) return <div className="wrap"><ErrorNote error={error} />{!error && <p className="muted">Loading…</p>}</div>;

  return (
    <div className="wrap">
      <header className="top">
        <div>
          <h1>AI Governance Crosswalk</h1>
          <p className="sub">AI system register, UK, EU, Australian and US applicability, and one control set mapped to ISO/IEC 42001, 23894, 42005, NIST AI RMF and AI6.</p>
        </div>
        <div className="who">
          <div className="row">
            {me.tenants.length > 0 && (
              <label className="row small">Workspace
                <select className="btn" id="tenant" value={tenantId ?? ""} onChange={(e) => setTenantId(e.target.value)}>
                  {me.tenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.region})</option>)}
                </select>
              </label>
            )}
            {me.platformAdmin && <button className="btn" onClick={() => setCreatingTenant((v) => !v)}>{creatingTenant ? "Cancel" : "New workspace"}</button>}
          </div>
          <span>{me.email}{role && ` · ${ROLE_LABELS[role].name}`}{me.platformAdmin && " · Practice admin"} · <button className="btn ghost small" onClick={() => keycloak.logout({ redirectUri: location.origin })}>Sign out</button></span>
          {library && <span className="mono">Library v{library.version} · regulatory baseline {library.baseline}</span>}
        </div>
      </header>

      {!me.disclaimer.acknowledgedAt ? (
        <DisclaimerGate onAccepted={() => setMe({ ...me, disclaimer: { ...me.disclaimer, acknowledgedAt: new Date().toISOString() } })} />
      ) : <>
      {creatingTenant && <NewWorkspace onCreated={async (id) => { setCreatingTenant(false); await loadMe(); setTenantId(id); toast("Workspace created"); }} />}

      {!tenant ? (
        <div className="panel empty">
          {me.platformAdmin ? <>
            <strong>There are no client workspaces yet.</strong>
            <span className="muted">Create the first one with New workspace. As a practice admin you see every workspace, so if you expected existing ones, this database doesn't contain them.</span>
          </> : <>
            <strong>You are not a member of any client workspace yet.</strong>
            <span className="muted">Ask your administrator to invite you using the email address you signed in with.</span>
          </>}
        </div>
      ) : (
        <>
          <nav className="tabs" role="tablist">
            {tabs.map(([k, label]) => <button key={k} role="tab" aria-selected={activeTab === k} onClick={() => go(k)}>{label}</button>)}
          </nav>
          <ErrorNote error={error} />
          <main>
            {activeTab === "register" && <RegisterView tenantId={tenant.id} systems={systems} allowed={allowed} toast={toast}
              onOpen={(id) => { setSelected(id); go("system"); }} onCreated={(s) => { upsert(s); setSelected(s.id); go("system"); }} />}
            {activeTab === "system" && <SystemView key={tenant.id} tenantId={tenant.id} systems={systems ?? []} selected={selected} onSelect={setSelected}
              allowed={allowed} toast={toast} onChange={upsert}
              onDeleted={(id) => { setSystems((c) => (c ?? []).filter((x) => x.id !== id)); setSelected(null); go("register"); toast("System deleted"); }}
              onConflict={loadSystems} />}
            {activeTab === "crosswalk" && library && <CrosswalkView library={library} platformAdmin={me.platformAdmin} onChange={loadLibrary} toast={toast} />}
            {activeTab === "members" && <MembersView tenantId={tenant.id} canManage={allowed("member.manage")} toast={toast} />}
            {activeTab === "audit" && <AuditView tenantId={tenant.id} />}
            {activeTab === "watch" && <WatchView toast={toast} />}
            {activeTab === "sources" && <SourcesView />}
          </main>
        </>
      )}
      </>}
      <DisclaimerFooter onReadMore={me.disclaimer.acknowledgedAt && tenant ? () => go("sources") : undefined} />
      {toastNode}
    </div>
  );
}
